import type {
  SeedanceModel,
  SeedanceResolution,
  VeoQuality,
  VideoAspectRatio,
  VideoDuration,
  VideoProvider,
  VideoSubMode,
} from '@/types/chat';
import type { ComposerSkillId } from '@/lib/api/skills';

import { UGC_MARKETING_STUDIO_PROJECT_RECIPES } from '@/lib/ugc-marketing-studio-project-recipes';

type LocalizedRecipeText = {
  en: string;
  zh: string;
};

export type UgcMarketingStudioRecipe = {
  skillId: Exclude<ComposerSkillId, 'brand-visual-video'>;
  prompt: LocalizedRecipeText;
  referenceImages: readonly {
    src: string;
    slot: 'person' | 'product';
    label: LocalizedRecipeText;
    alt: LocalizedRecipeText;
  }[];
  videoSettings: {
    videoProvider: VideoProvider;
    videoDuration: VideoDuration;
    videoSubMode: VideoSubMode;
    videoAspectRatio: VideoAspectRatio;
    seedanceModel?: SeedanceModel;
    seedanceResolution?: SeedanceResolution;
    veoQuality?: VeoQuality;
    generateAudio?: boolean;
  };
  modelLabel: string;
  resolution: string;
  outputSize: string;
  createdAt: string;
  sourceProjectId?: string;
  sourceTaskId?: string;
};

export type UgcMarketingStudioMedia = {
  id: string;
  src: string;
  /** Original width / height, retained as media metadata for previews and details. */
  aspectRatio: number;
  recipe?: UgcMarketingStudioRecipe;
};

/**
 * Shared UGC Marketing Studio showcase used by Explore and the login landing
 * pages. Keep ordering here as the single source of truth for every surface.
 */
export const UGC_MARKETING_STUDIO_MEDIA: readonly UgcMarketingStudioMedia[] = [
  {
    id: 'makeup',
    src: '/ugc-marketing-studio/makeup-demo.mp4',
    aspectRatio: 720 / 1280,
    recipe: UGC_MARKETING_STUDIO_PROJECT_RECIPES.makeup,
  },
  {
    id: 'case-01',
    src: '/ugc-marketing-studio/case-01.mp4',
    aspectRatio: 496 / 864,
    recipe: UGC_MARKETING_STUDIO_PROJECT_RECIPES['case-01'],
  },
  {
    id: 'bag',
    src: '/ugc-marketing-studio/bag-demo.mp4',
    aspectRatio: 496 / 864,
    recipe: UGC_MARKETING_STUDIO_PROJECT_RECIPES.bag,
  },
  {
    id: 'case-02',
    src: '/ugc-marketing-studio/case-02.mp4',
    aspectRatio: 496 / 864,
    recipe: UGC_MARKETING_STUDIO_PROJECT_RECIPES['case-02'],
  },
  {
    id: 'outfit',
    src: '/ugc-marketing-studio/outfit-demo.mp4',
    aspectRatio: 496 / 864,
    recipe: UGC_MARKETING_STUDIO_PROJECT_RECIPES.outfit,
  },
  {
    id: 'case-03',
    src: '/ugc-marketing-studio/case-03.mp4',
    aspectRatio: 496 / 864,
    recipe: UGC_MARKETING_STUDIO_PROJECT_RECIPES['case-03'],
  },
  {
    id: 'case-04',
    src: '/ugc-marketing-studio/case-04.mp4',
    aspectRatio: 720 / 1280,
    recipe: UGC_MARKETING_STUDIO_PROJECT_RECIPES['case-04'],
  },
  {
    id: 'case-05',
    src: '/ugc-marketing-studio/case-05.mp4',
    aspectRatio: 496 / 864,
    recipe: {
      skillId: 'ugc-try-on',
      prompt: {
        en: 'Use image1 as the only creator identity, body, room, and lighting reference. Use image2 as the only authority for the complete selected outfit. Create an 8-second vertical 9:16 creator-led virtual try-on that feels like spontaneous flagship-phone footage in one lived-in dressing space. Preserve the creator’s exact face, blonde wavy curtain-parted hair, body proportions, and natural movement. Preserve every proven garment detail in the selected look: the charcoal-and-camel fuzzy argyle V-neck sweater, cream striped shirt, soft pink tie, dark brown wide-leg corduroy trousers, and polished brown leather loafers. Start with a readable waist-up creator shot, move to a clear full-body front and side fit check with one natural step, then return to a medium creator reaction. Use ordinary 1× movement and clean hard cuts only—no morph, dissolve, spin, speed ramp, digital zoom, duplicated body, wardrobe transformation, captions, CTA, watermark, or new text. Keep daylight, mild phone grain, slight focus and exposure settling, imperfect framing, real shadows, and quiet room sound. Fabric must follow gravity and body contact; identity, anatomy, patterns, seams, closures, hems, materials, colors, and footwear must remain stable in every frame.',
        zh: '使用 image1 作为唯一的人物身份、身形、房间与光线参考；使用 image2 作为整套穿搭的唯一依据。生成一支 8 秒、9:16 竖屏的创作者虚拟试穿视频，像在同一个真实生活化更衣空间里用旗舰手机随手拍摄。严格保留人物的脸部身份、金色中长波浪中分发型、身体比例与自然动作；严格保留整套穿搭中已被参考图证明的细节：深灰与驼棕色毛绒菱格 V 领针织衫、米白条纹衬衫、浅粉领带、深棕宽腿灯芯绒长裤和抛光棕色皮乐福鞋。开场用清晰的半身人物镜头，中段完整展示全身正面、侧面与自然走一步的上身效果，结尾回到中景人物反应。只使用正常 1 倍速动作和干净硬切；禁止变形、溶解、旋转、变速、数字变焦、人物复制、服装变装过程、字幕、CTA、水印或新增文字。保留自然日光、轻微手机颗粒、短暂对焦与曝光调整、不完美构图、真实阴影和安静室内环境声。布料必须符合重力与身体接触，人物身份、人体结构、图案、缝线、开合、下摆、材质、颜色和鞋履在所有帧中保持稳定。',
      },
      referenceImages: [
        {
          src: 'https://img.dobidobi.cn/materials/76855434-9c51-44aa-a70c-fda8b50228c2/general/586187db-b0e2-4a27-bd26-59342f6b73bc.png',
          slot: 'person',
          label: {
            en: 'Person',
            zh: '人物',
          },
          alt: {
            en: 'Creator identity, body, room, and lighting reference',
            zh: '人物身份、身形、房间与光线参考图',
          },
        },
        {
          src: 'https://img.dobidobi.cn/materials/76855434-9c51-44aa-a70c-fda8b50228c2/general/5bba7dcb-fdd7-4978-97dc-fdcbbb968ae3.jpg',
          slot: 'product',
          label: {
            en: 'Outfit',
            zh: '穿搭',
          },
          alt: {
            en: 'Complete selected outfit reference',
            zh: '完整选定穿搭参考图',
          },
        },
      ],
      videoSettings: {
        videoProvider: 'seedance',
        videoDuration: '8',
        videoSubMode: 'universal_ref',
        videoAspectRatio: '9:16',
        seedanceModel: 'seedance-2.0',
        seedanceResolution: '480P',
      },
      modelLabel: 'Seedance 2.0',
      resolution: '480p',
      outputSize: '496 × 864',
      createdAt: '2026-07-30',
      sourceProjectId: '8a746125-a19c-43b7-a3ee-475bb90e8620',
      sourceTaskId: '95d90023-9b0b-4363-a6d8-9b12146a8694',
    },
  },
  {
    id: 'case-06',
    src: '/ugc-marketing-studio/case-06.mp4',
    aspectRatio: 720 / 1280,
    recipe: UGC_MARKETING_STUDIO_PROJECT_RECIPES['case-06'],
  },
];
